export const CONTACT_POSITIONS = ['header', 'detail', 'passcode'] as const;
export type AnalyticsKind = 'pv' | 'detail' | 'contact';
export interface AnalyticsEvent {
  id: string;
  visitor: string;
  visit: string;
  kind: AnalyticsKind;
  topicId?: string;
  position?: (typeof CONTACT_POSITIONS)[number];
}
export const ANALYTICS_RANGES = ['today', '7d', '30d'] as const;
export type AnalyticsRange = (typeof ANALYTICS_RANGES)[number];
/** 明细保留天数；看板最多回看30天，再加上一期对比，留足余量。 */
export const ANALYTICS_RETENTION_DAYS = 365;
/** 快到期：有效且这么多天内（含当天）到期。 */
export const EXPIRING_DAYS = 15;
/** 开通不满这么多天的会员还来不及使用，不算沉睡。 */
export const DORMANT_GRACE_DAYS = 7;

export function beijingDay(now = new Date()): string {
  return new Date(now.getTime() + 8 * 3600000).toISOString().slice(0, 10);
}
export function addDays(day: string, days: number): string {
  return new Date(Date.parse(day + 'T00:00:00Z') + days * 86400000)
    .toISOString()
    .slice(0, 10);
}
/** 当前区间与紧挨着的上一期（同样天数），都按北京时间整天计算。 */
export function rangeDays(range: AnalyticsRange, today = beijingDay()) {
  const days = range === 'today' ? 1 : range === '7d' ? 7 : 30;
  const from = addDays(today, 1 - days);
  return {
    days,
    from,
    to: today,
    previousFrom: addDays(from, -days),
    previousTo: addDays(from, -1),
  };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function parseAnalyticsEvent(input: unknown): AnalyticsEvent {
  if (!input || typeof input !== 'object' || Array.isArray(input))
    throw new Error('无效事件');
  const e = input as Record<string, unknown>;
  // 旧版缓存页面仍可能带 channel 字段，接受但不再保存。
  const common = ['id', 'visitor', 'visit', 'kind', 'channel'];
  const extras: Record<string, string[]> = {
    pv: [],
    detail: ['topicId'],
    contact: ['position'],
  };
  if (
    typeof e.kind !== 'string' ||
    !Object.prototype.hasOwnProperty.call(extras, e.kind)
  )
    throw new Error('无效事件');
  if (
    Object.keys(e).some(
      (key) => ![...common, ...extras[e.kind as string]].includes(key),
    )
  )
    throw new Error('无效字段');
  for (const key of ['id', 'visitor', 'visit'])
    if (typeof e[key] !== 'string' || !UUID.test(e[key] as string))
      throw new Error('无效编号');
  if (
    e.kind === 'detail' &&
    (typeof e.topicId !== 'string' || !UUID.test(e.topicId))
  )
    throw new Error('无效主题');
  if (e.kind === 'contact' && !CONTACT_POSITIONS.includes(e.position as any))
    throw new Error('无效入口');
  return {
    id: e.id,
    visitor: e.visitor,
    visit: e.visit,
    kind: e.kind,
    ...(e.kind === 'detail' ? { topicId: e.topicId } : {}),
    ...(e.kind === 'contact' ? { position: e.position } : {}),
  } as AnalyticsEvent;
}
export function classifyRejection(
  reason: string | null,
): 'passcode' | 'quota' | 'other' {
  if (
    ['兑换码无效', '账号已冻结', '会员已过期', '兑换码已作废'].includes(
      reason || '',
    )
  )
    return 'passcode';
  if (['超出月配额', '超出单日上限'].includes(reason || '')) return 'quota';
  return 'other';
}

export interface AnalyticsPeriodNumbers {
  /** 访客：同一浏览器只算一次 */
  visitors: number;
  /** 打开过主题详情的访客 */
  detailVisitors: number;
  /** 点过客服入口的访客 */
  contactVisitors: number;
  /** 成功领取过资源的会员 */
  claimMembers: number;
  /** 成功领取的资源数（同一会员同一主题只算一次） */
  claims: number;
  /** 新开通的会员（按激活时间，老会员按创建时间；未激活、作废不算） */
  newMembers: number;
}
export interface AnalyticsMemberRow {
  passcode: string;
  /** 本期领取资源数 */
  claims: number;
  lastClaimDay: string | null;
  expiryDate: string;
}
export interface AnalyticsSummary {
  range: AnalyticsRange;
  from: string;
  to: string;
  current: AnalyticsPeriodNumbers;
  previous: AnalyticsPeriodNumbers;
  /** 近30天每天的情况，固定30行，从新到旧 */
  daily: Array<{
    day: string;
    visitors: number;
    claimMembers: number;
    claims: number;
  }>;
  members: {
    /** 当前有效会员（启用中且未过期；冻结、过期、未激活、作废都不算） */
    valid: number;
    /** 有效会员中本期领取过的人数 */
    activeValid: number;
    /** 有效会员中开通满 DORMANT_GRACE_DAYS 天、近30天一次都没领取的人数 */
    dormant: number;
    /** 有效会员中 EXPIRING_DAYS 天内到期的人数 */
    expiring: number;
  };
  /** 本期领取最多的会员，最多10位 */
  topMembers: AnalyticsMemberRow[];
  /** 快到期的有效会员，全部，到期早的在前 */
  expiringMembers: AnalyticsMemberRow[];
  /** 沉睡会员（定义同 members.dormant），全部，到期早的在前 */
  dormantMembers: AnalyticsMemberRow[];
  /** 本期最受关注的主题，最多10个 */
  topTopics: Array<{ name: string; viewers: number; claims: number }>;
  /** 本期领取失败次数 */
  failures: { passcode: number; quota: number; other: number };
}
